/* eslint-disable no-underscore-dangle */
/**
 * The offer to close a booking the moment its last trip claim is dealt with.
 *
 * It is an OFFER, not a step: declining costs one click, writes nothing and is
 * never asked again. The decision that opened it is already committed — the
 * review call returned before this was rendered — so nothing here can undo it,
 * and a completion that fails leaves an approved claim approved.
 *
 * The completion itself goes through `completeBooking`, the one write every
 * "Mark Completed" in the dashboard uses, so this cannot drift from the Orders
 * screen's version of the same action.
 *
 * A race is expected rather than exceptional: a driver can file a receipt
 * between the check and the click. The API answers that with the same 409 it
 * answers "Mark Completed" with, and this says so in the API's own words,
 * withdraws the button and leaves the booking exactly as it was. It never
 * reports a completion that did not happen.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Modal } from 'react-bootstrap';
import { completeBooking } from '../../Pages/Orders/Functions/markAsCompleted';
import { tripClaimsLink, tripClaimsRefusalOf } from '../../Utils/tripClaims';

const errorText = (err) => err?.response?.data?.message
  || err?.message
  || 'Could not mark the booking completed';

function CompleteBookingOffer({ order, onCompleted, onClose }) {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [error, setError] = useState('');

  const reference = order?.booking_reference || 'this booking';
  const bookingId = order?._id;

  const confirm = async () => {
    setBusy(true);
    setError('');
    setRefusal(null);
    try {
      await completeBooking(order);
      onCompleted?.(order);
    } catch (err) {
      // The API's 409 while trip claims wait, in its own words. Anything else
      // is reported as itself. Either way the booking is untouched and the
      // caller is NOT told it completed.
      const refused = tripClaimsRefusalOf(err);
      if (refused) setRefusal(refused);
      else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered data-testid="complete-booking-offer">
      <Modal.Header closeButton>
        <Modal.Title className="h5">Mark this booking completed?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          {`Nothing else is waiting for review on ${reference}.`}
        </p>
        <p className="text-muted small mb-0">
          Left alone it stays open until the nightly job closes it, at least 48 hours
          after the trip ends. Completing it now sends the post-trip email and releases
          it for final invoicing.
        </p>

        {refusal && (
          <Alert variant="warning" className="mt-3 mb-0" data-testid="complete-booking-refused">
            <div className="fw-semibold">{`${reference} was not completed`}</div>
            <div className="small">{refusal.message}</div>
            <div className="small">
              Something was filed while this was open — the booking is unchanged.
            </div>
            <Link
              to={tripClaimsLink(refusal.bookingId || bookingId)}
              className="small fw-semibold"
              onClick={onClose}
            >
              Review it
              <i className="fa fa-arrow-right ms-1" />
            </Link>
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mt-3 mb-0" data-testid="complete-booking-error">
            <div className="fw-semibold">{`${reference} was not completed`}</div>
            <div className="small">{error}</div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={busy}>
          {refusal ? 'Close' : 'Not now'}
        </Button>
        {!refusal && (
          <Button variant="success" onClick={confirm} disabled={busy}>
            <i className="fa fa-check me-1" />
            Mark Completed
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default CompleteBookingOffer;
